import React, { useState, useEffect } from 'react';
import { AlertTriangle, Copy, ChevronDown, ChevronUp, MessageSquare, CheckCircle, Send } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../utils/api';

// Auto-send error to backend
const sendErrorToBackend = async (errorDetails) => {
  try {
    await api.post('/public/log-error', errorDetails);
    console.log('[ErrorDisplay] Error logged to backend');
  } catch (e) {
    console.warn('[ErrorDisplay] Failed to log error to backend:', e);
  }
};

// Global Error Display Component
export const ErrorDisplay = ({ 
  error, 
  title = 'حدث خطأ', 
  onRetry = null,
  showDetails = true,
  autoSend = true,
  section = null
}) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);
  
  // Format error message
  const errorMessage = typeof error === 'string' 
    ? error 
    : error?.message || error?.detail || 'خطأ غير معروف';
  
  // Full error details for copying
  const errorDetails = {
    message: errorMessage,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    stack: error?.stack || 'N/A',
    response: error?.response?.data || null,
    context: { section }
  };
  
  // Auto-send error to backend on mount
  useEffect(() => {
    if (autoSend && !sent) {
      sendErrorToBackend(errorDetails);
      setSent(true);
    }
  }, [autoSend, sent]);
  
  const copyError = async () => {
    const textToCopy = JSON.stringify(errorDetails, null, 2);
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.success('تم نسخ تفاصيل الخطأ');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success('تم نسخ تفاصيل الخطأ');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `🚨 خطأ في التطبيق:\n\n${errorMessage}\n\nالصفحة: ${window.location.href}\n\nالوقت: ${new Date().toLocaleString('ar-AE')}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };
  
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 my-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-red-400 mb-1">{title}</h4>
          <p className="text-sm text-muted-foreground break-words">{errorMessage}</p>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              onClick={copyError}
              size="sm"
              variant="outline"
              className={`border-red-500/30 ${copied ? 'text-green-400' : 'text-red-400'} hover:bg-red-500/10`}
            >
              {copied ? <CheckCircle className="w-4 h-4 me-1" /> : <Copy className="w-4 h-4 me-1" />}
              {copied ? 'تم النسخ' : 'نسخ الخطأ'}
            </Button>
            
            <Button
              onClick={openWhatsApp}
              size="sm"
              variant="outline"
              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
            >
              <MessageSquare className="w-4 h-4 me-1" />
              إرسال للدعم
            </Button>
            
            {onRetry && (
              <Button
                onClick={onRetry}
                size="sm"
                className="bg-primary hover:bg-primary/90"
              >
                إعادة المحاولة
              </Button>
            )}
          </div>
          
          {/* Expandable Details */}
          {showDetails && (
            <div className="mt-3">
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center text-xs text-muted-foreground hover:text-white transition-colors"
              >
                {expanded ? <ChevronUp className="w-4 h-4 me-1" /> : <ChevronDown className="w-4 h-4 me-1" />}
                {expanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
              </button>
              
              {expanded && (
                <pre className="mt-2 p-3 bg-black/30 rounded-lg text-xs overflow-x-auto text-muted-foreground" dir="ltr">
                  {JSON.stringify(errorDetails, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Error Boundary Component
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught:', error, errorInfo);
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <ErrorDisplay 
              error={this.state.error}
              title="حدث خطأ في التطبيق"
              onRetry={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
            />
          </div>
        </div>
      );
    }
    
    return this.props.children;
  }
}

// Toast error helper with auto-send
export const showErrorToast = (error, title = 'خطأ', section = null) => {
  const message = typeof error === 'string' 
    ? error 
    : error?.message || error?.detail || 'حدث خطأ غير متوقع';
  
  // Auto-send to backend
  const errorInfo = {
    message,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    stack: error?.stack || null,
    response: error?.response?.data || null,
    context: { section, title }
  };
  
  // Send to backend silently
  api.post('/public/log-error', errorInfo).catch(() => {});
  
  toast.error(title, {
    description: message,
    action: {
      label: 'نسخ',
      onClick: () => {
        navigator.clipboard.writeText(JSON.stringify(errorInfo, null, 2));
        toast.success('تم نسخ تفاصيل الخطأ');
      }
    },
    duration: 8000
  });
};

export default ErrorDisplay;
